import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Activity, Sun, Moon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import MfaVerify from "@/components/MfaVerify";
import MfaSetup from "@/components/MfaSetup";

export default function Login() {
  const { user, login, completeLogin } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("password"); // password | mfa | setup
  const [challenge, setChallenge] = useState("");

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await login(email.trim().toLowerCase(), password);
      if (data.mfa_required) { setChallenge(data.challenge); setPhase("mfa"); }
      else if (data.mfa_setup_required) { setChallenge(data.challenge); setPhase("setup"); }
      else navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const finish = (data) => {
    completeLogin(data.token, data.user);
    navigate("/");
  };

  return (
    <div className="app-bg min-h-screen flex items-center justify-center px-4">
      <button onClick={toggle} data-testid="theme-toggle-login" className="absolute top-5 right-5 p-2 rounded-lg text-muted-foreground hover:text-cyan-400 hover:bg-white/5 transition-colors">
        {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
      </button>
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <Activity className="w-6 h-6 text-cyan-400" />
          <span className="font-head font-black text-2xl tracking-tight">GLH<span className="text-cyan-400">·</span>IDT Referral Tracker</span>
        </div>

        <div data-testid="auth-warning-banner" className="rounded-xl px-4 py-2.5 mb-4 border border-white/10 bg-white/5 text-[11px] leading-relaxed text-muted-foreground font-mono">
          Secure internal system for authorized Goodwin Living staff. Handles protected health information (PHI); activity is logged to help keep patient data safe.
        </div>

        {phase === "mfa" && <MfaVerify challenge={challenge} onDone={finish} />}
        {phase === "setup" && <MfaSetup challenge={challenge} onDone={finish} />}

        {phase === "password" && (
          <form onSubmit={submit} className="glass glass-cyan rounded-2xl p-7" data-testid="login-form">
            <h1 className="font-head font-bold text-lg mb-1">Sign in</h1>
            <p className="text-xs text-muted-foreground mb-5 font-mono">Internal · role-based · HIPAA · 2FA</p>

            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="bg-slate-900/60 border-white/10" autoComplete="username" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">Password</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="bg-slate-900/60 border-white/10" autoComplete="current-password" />
              </div>
            </div>

            <Button type="submit" disabled={busy} data-testid="login-submit" className="w-full mt-5 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">
              {busy ? "Signing in…" : "Sign in"}
            </Button>

            <div className="flex items-center justify-end mt-3 text-xs font-mono">
              <Link to="/forgot-password" className="text-muted-foreground hover:text-foreground" data-testid="link-forgot">Forgot password?</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
